import Component from '@glimmer/component';

// ---------------------------------------------------------------------------
// Timeline – vertical timeline with customizable nodes and content
// ---------------------------------------------------------------------------

interface TimelineSignature<T = unknown> {
  Args: {
    /** Array of items to render in the timeline */
    items: T[];
    /** Custom class for the vertical line */
    lineClass?: string;
    /** Custom class for the list container */
    listClass?: string;
    /** Custom class for each item wrapper */
    itemClass?: string;
    /** Custom class for the node container */
    nodeClass?: string;
    /** Custom class for the content container */
    contentClass?: string;
  };
  Blocks: {
    node?: [item: T, index: number];
    content?: [item: T, index: number];
    default?: [item: T, index: number];
  };
  Element: HTMLDivElement;
}

export default class Timeline<T = unknown> extends Component<TimelineSignature<T>> {
  get lineClass() {
    return this.args.lineClass ?? 'bg-violet-200';
  }

  get listClass() {
    return this.args.listClass ?? 'space-y-4';
  }

  get itemClass() {
    return this.args.itemClass ?? 'relative flex items-start gap-4';
  }

  get nodeClass() {
    return this.args.nodeClass ?? 'absolute -left-8';
  }

  get contentClass() {
    return this.args.contentClass ?? 'flex-1';
  }

  <template>
    <div class="relative pl-12" ...attributes>
      <div class="absolute left-[18px] top-0 bottom-0 w-0.5 {{this.lineClass}}"></div>
      <div class="{{this.listClass}}">
        {{#each @items as |item index|}}
          <div class="{{this.itemClass}}">
            {{#if (has-block "node")}}
              <div class="{{this.nodeClass}}">
                {{yield item index to="node"}}
              </div>
            {{/if}}
            <div class="{{this.contentClass}}">
              {{#if (has-block "content")}}
                {{yield item index to="content"}}
              {{else}}
                {{yield item index}}
              {{/if}}
            </div>
          </div>
        {{/each}}
      </div>
    </div>
  </template>
}
